import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardHeader,
  Checkbox,
  EmptyState,
  ErrorState,
  InlineMessage,
  LoadingState,
} from '../components';
import { ExportDialog } from '../features/exports/ExportDialog';
import { ProfileCandidateList } from '../features/profiles/ProfileCandidateList';
import { ResultCard } from '../features/results/ResultCard';
import { ResultFiltersBar } from '../features/results/ResultFilters';
import { JobStatusBadge } from '../features/search/JobStatusBadge';
import type { Platform } from '../types/api';
import { ResultDetailPanel } from '../features/results/ResultDetailPanel';
import { useProfileCandidates, useSelectProfiles } from '../hooks/use-profiles';
import { useResultFilters } from '../hooks/use-result-filters';
import { useResultSelection } from '../hooks/use-result-selection';
import {
  isTerminalStatus,
  useCancelSearch,
  useResults,
  useSearchDetail,
} from '../hooks/use-searches';
import { describeError, messageForCode } from '../services/errors';

export function SearchDetailPage() {
  const { searchId } = useParams<{ searchId: string }>();
  const search = useSearchDetail(searchId);
  const cancel = useCancelSearch(searchId ?? '');
  const { filters, setFilter, clear, hasActiveFilters } = useResultFilters();
  const selection = useResultSelection(filters);
  const [openResultId, setOpenResultId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const status = search.data?.status;
  // Só o que o fornecedor informou: `null` vira omissão, nunca palpite.
  const consultados = (search.data?.job?.effectiveQueries ?? []).filter(
    (item): item is { platform: Platform; query: string } => item.query !== null,
  );
  const awaitingSelection = status === 'AWAITING_SELECTION';
  const running = !isTerminalStatus(status) && !awaitingSelection;
  const results = useResults(searchId, filters, status);
  const candidates = useProfileCandidates(searchId, awaitingSelection);
  const selectProfiles = useSelectProfiles(searchId ?? '');

  if (search.isPending) {
    return <LoadingState label="Carregando pesquisa" />;
  }

  if (search.isError) {
    return (
      <ErrorState message={describeError(search.error)} onRetry={() => void search.refetch()} />
    );
  }

  const detail = search.data;
  const items = results.data?.pages.flatMap((page) => page.data) ?? [];
  const loadedIds = items.map((result) => result.id);

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader
          title={detail.label ?? detail.terms.join(', ') ?? 'Pesquisa'}
          description={`${detail.platforms.join(' · ')} — ${detail.resultCount} resultado(s)`}
          action={
            <div className="flex items-center gap-3">
              <JobStatusBadge status={detail.status} />
              {(detail.status === 'PENDING' || detail.status === 'RUNNING') && (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={cancel.isPending}
                  onClick={() => cancel.mutate()}
                >
                  Cancelar
                </Button>
              )}
            </div>
          }
        />

        {/*
          O que foi **efetivamente** consultado (SPEC-025 RF-Q08). Sem isto, a
          diferença entre o que o usuário pediu e o que o fornecedor executou só
          existia no log do fornecedor — e foi assim que uma pesquisa por
          "eleições sp" virou a leitura de uma hashtag colada, sem ninguém ver.
        */}
        {consultados.length > 0 && (
          <p className="text-sm text-text-muted">
            Pesquisamos por{' '}
            {consultados.map((item, index) => (
              <span key={item.platform}>
                {index > 0 && ', '}
                <strong className="text-text">{item.query}</strong> em {item.platform}
              </span>
            ))}
            .
          </p>
        )}

        {/* Comunicação honesta de cada estado terminal (SPEC-014 §9). */}
        {detail.status === 'PARTIAL' && (
          <InlineMessage tone="warning">
            {/*
              Quando há código, ele é mais específico que a frase genérica: numa
              recusa de alvo a plataforma respondeu muito bem, e dizer que ela
              "não respondeu" seria trocar uma mentira por outra (SPEC-024).
            */}
            {detail.errorCode
              ? messageForCode(detail.errorCode)
              : 'Parte das plataformas não respondeu. Os resultados abaixo estão incompletos.'}
          </InlineMessage>
        )}
        {detail.status === 'FAILED' && (
          <InlineMessage tone="danger">
            {detail.errorCode
              ? messageForCode(detail.errorCode)
              : 'A coleta não pôde ser concluída.'}
          </InlineMessage>
        )}
        {detail.status === 'CANCELLED' && (
          <InlineMessage tone="info">Esta pesquisa foi cancelada.</InlineMessage>
        )}
        {running && (
          <InlineMessage tone="info">
            Coletando conteúdo. Esta tela se atualiza sozinha.
          </InlineMessage>
        )}
        {awaitingSelection && (
          <InlineMessage tone="warning">
            Encontramos mais de um perfil possível. Nada foi coletado ainda — escolha abaixo de quem
            você quer pesquisar.
          </InlineMessage>
        )}
        {cancel.isError && (
          <InlineMessage tone="danger">{describeError(cancel.error)}</InlineMessage>
        )}
      </Card>

      {awaitingSelection && (
        <>
          {candidates.isPending && <LoadingState label="Carregando perfis encontrados" />}
          {candidates.isError && (
            <ErrorState
              message={describeError(candidates.error)}
              onRetry={() => void candidates.refetch()}
            />
          )}
          {candidates.data && (
            <ProfileCandidateList
              candidates={candidates.data.candidates}
              tiedAtTop={candidates.data.tiedAtTop}
              submitting={selectProfiles.isPending}
              error={selectProfiles.isError ? describeError(selectProfiles.error) : null}
              onConfirm={(profileIds, save) => selectProfiles.mutate({ profileIds, save })}
            />
          )}
        </>
      )}

      <ResultFiltersBar
        filters={filters}
        onChange={setFilter}
        onClear={clear}
        hasActiveFilters={hasActiveFilters}
        disabled={items.length === 0 && !hasActiveFilters}
      />

      {!results.isPending && !results.isError && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-surface px-4 py-3">
          <Checkbox
            label="Selecionar os resultados carregados"
            checked={loadedIds.length > 0 && loadedIds.every((id) => selection.isSelected(id))}
            disabled={loadedIds.length === 0}
            onChange={() => selection.toggleAllLoaded(loadedIds)}
          />

          <span aria-live="polite" className="text-sm text-text-muted">
            {selection.count > 0
              ? `${selection.count} selecionado(s)`
              : 'Sem seleção — a exportação inclui todo o recorte filtrado'}
          </span>

          <div className="ml-auto flex items-center gap-2">
            {selection.count > 0 && (
              <Button variant="ghost" size="sm" onClick={selection.clear}>
                Limpar seleção
              </Button>
            )}
            <Button size="sm" disabled={items.length === 0} onClick={() => setExporting(true)}>
              Exportar
            </Button>
          </div>
        </div>
      )}

      <section aria-label="Resultados" className="flex flex-col gap-3">
        {results.isPending && <LoadingState label="Carregando resultados" />}

        {results.isError && (
          <ErrorState
            message={describeError(results.error)}
            onRetry={() => void results.refetch()}
          />
        )}

        {!results.isPending && !results.isError && items.length === 0 && (
          <EmptyState
            title={running ? 'Ainda sem resultados' : 'Nenhum resultado encontrado'}
            description={
              running
                ? 'A coleta está em andamento. Os resultados aparecem aqui conforme chegam.'
                : hasActiveFilters
                  ? 'Nenhum resultado corresponde aos filtros aplicados.'
                  : // Job com código de erro já foi explicado pelo aviso acima.
                    // Repetir aqui "não retornou conteúdo público" afirmaria que
                    // procuramos e não achamos, quando nem chegamos a ler (SPEC-024).
                    detail.errorCode !== null
                    ? 'A coleta não chegou a trazer conteúdo. Veja o aviso acima.'
                    : 'Esta pesquisa não retornou conteúdo público no período escolhido.'
            }
            action={
              hasActiveFilters ? (
                <Button variant="secondary" size="sm" onClick={clear}>
                  Limpar filtros
                </Button>
              ) : undefined
            }
          />
        )}

        {items.map((result) => (
          <ResultCard
            key={result.id}
            result={result}
            onOpen={setOpenResultId}
            selected={selection.isSelected(result.id)}
            onToggleSelect={selection.toggle}
          />
        ))}

        {results.hasNextPage && (
          <div className="flex justify-center py-2">
            <Button
              variant="secondary"
              loading={results.isFetchingNextPage}
              onClick={() => void results.fetchNextPage()}
            >
              Carregar mais
            </Button>
          </div>
        )}
      </section>

      {openResultId && (
        <ResultDetailPanel resultId={openResultId} onClose={() => setOpenResultId(null)} />
      )}

      {exporting && searchId && (
        <ExportDialog
          searchId={searchId}
          filters={filters}
          selectedIds={selection.selectedIds}
          searchStatus={detail.status}
          onClose={() => setExporting(false)}
        />
      )}
    </div>
  );
}
